/**
 * Data Fetcher Service
 * Centralized service for fetching data with deduplication, batching, and caching
 */

import { executeGraphQLQuery, executeBatchGraphQLQueries } from './optixApi.js'
import { rateLimitManager } from './rateLimitManager.js'

class DataFetcher {
  constructor() {
    // Cache for storing responses
    this.cache = new Map()
    
    // Pending requests map for deduplication (key -> promise)
    this.pendingRequests = new Map()
    
    // Batch queue for GraphQL queries
    this.batchQueue = []
    this.batchTimeout = null
    this.batchDelay = 50 // ms to wait before batching
    
    // Default cache TTL (5 minutes)
    this.defaultTTL = 5 * 60 * 1000
  }

  /**
   * Generates a cache key from query and variables
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {string}
   */
  getCacheKey(query, variables = {}) {
    // Ensure variables is a plain object (not a ref/computed)
    // Variables should already be unwrapped by the time they reach here,
    // but add defensive check just in case
    let plainVariables = variables
    if (variables && typeof variables === 'object' && 'value' in variables) {
      // If it's a Vue ref/computed, unwrap it
      plainVariables = variables.value || {}
    }
    
    try {
      const variablesStr = JSON.stringify(plainVariables)
      return `${query}:${variablesStr}`
    } catch (e) {
      // Fallback if stringify fails (shouldn't happen with proper unwrapping)
      console.warn('Failed to stringify variables for cache key:', e)
      return `${query}:${Date.now()}` // Use timestamp as fallback
    }
  }

  /**
   * Gets cached data if available and not expired
   * @param {string} key - Cache key
   * @returns {Object|null} Cached data or null
   */
  getCached(key) {
    const cached = this.cache.get(key)
    if (!cached) {
      return null
    }

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key)
      return null
    }

    return cached.data
  }

  /**
   * Stores data in cache
   * @param {string} key - Cache key
   * @param {*} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds
   */
  setCache(key, data, ttl = null) {
    const expiresAt = Date.now() + (ttl || this.defaultTTL)
    this.cache.set(key, {
      data,
      expiresAt
    })
  }

  /**
   * Invalidates cache entry
   * @param {string} key - Cache key (optional, clears all if not provided)
   */
  invalidateCache(key = null) {
    if (key) {
      this.cache.delete(key)
    } else {
      this.cache.clear()
    }
  }

  /**
   * Processes batch queue
   */
  async processBatchQueue() {
    if (this.batchQueue.length === 0) {
      return
    }

    const allItems = [...this.batchQueue]
    this.batchQueue = []
    this.batchTimeout = null

    // Group items by batchGroup (section index, or 'default' for ungrouped)
    const groups = new Map()
    for (const item of allItems) {
      const key = item.batchGroup ?? 'default'
      if (!groups.has(key)) {
        groups.set(key, [])
      }
      groups.get(key).push(item)
    }

    // Process each group as a separate batch request (in parallel)
    const batchPromises = []
    for (const [, groupItems] of groups) {
      // Sort by priority within each group
      const sortedBatch = groupItems.sort((a, b) => {
        return (b.priority || 0) - (a.priority || 0)
      })

      const queries = sortedBatch.map(item => ({
        query: item.query,
        variables: item.variables,
        operationName: item.operationName
      }))

      batchPromises.push(
        (async () => {
          try {
            if (!rateLimitManager.canMakeRequest()) {
              const result = await rateLimitManager.queueRequest(
                () => this.executeBatch(queries),
                Math.max(...sortedBatch.map((item) => item.priority || 0))
              )
              rateLimitManager.decrement()
              this.handleBatchResponse(sortedBatch, result)
            } else {
              const result = await this.executeBatch(queries)
              rateLimitManager.decrement()
              this.handleBatchResponse(sortedBatch, result)
            }
          } catch (error) {
            if (error.response?.status === 400 || error.response?.status === 413) {
              console.warn('Batch rejected, falling back to individual requests')
              await this.processBatchIndividually(sortedBatch)
            } else {
              sortedBatch.forEach((item) => item.reject(error))
            }
          }
        })()
      )
    }

    await Promise.all(batchPromises)
  }

  /**
   * Executes a batch of queries
   * @param {Array} queries - Array of query objects
   * @returns {Promise}
   */
  async executeBatch(queries) {
    const result = await executeBatchGraphQLQueries(queries)
    
    // Update rate limit manager with headers
    if (result.headers) {
      rateLimitManager.updateFromHeaders(result.headers)
    }
    
    return result
  }

  /**
   * Processes batch queue items individually with throttling to avoid rate limits.
   * API rejects batch format, so we send each query as a separate request.
   * @param {Array} batch - Batch items to process
   */
  async processBatchIndividually(batch) {
    // 60 requests/min = ~1/sec - throttle to stay under limit
    const CONCURRENCY = 2
    const DELAY_MS = 2100

    for (let i = 0; i < batch.length; i += CONCURRENCY) {
      const chunk = batch.slice(i, i + CONCURRENCY)
      await Promise.all(
        chunk.map(async (item) => {
          try {
            const result = await this.fetchGraphQL(
              item.query,
              item.variables,
              item.operationName,
              item.priority,
              false // Don't batch individual retries
            )
            item.resolve(result)
          } catch (error) {
            item.reject(error)
          }
        })
      )
      if (i + CONCURRENCY < batch.length) {
        await new Promise((r) => setTimeout(r, DELAY_MS))
      }
    }
  }

  /**
   * Handles batch response and resolves individual promises
   * @param {Array} batch - Original batch items
   * @param {Object} result - Batch response
   */
  handleBatchResponse(batch, result) {
    // GraphQL batch responses are typically an array of response objects
    const responses = Array.isArray(result.data) ? result.data : [result.data]
    
    batch.forEach((item, index) => {
      const response = responses[index]
      if (!response) {
        item.reject(new Error(`No response for query ${index}`))
        return
      }
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        item.reject(new Error(`GraphQL Error: ${JSON.stringify(response.errors)}`))
        return
      }
      
      // Extract data from response (could be response.data or just response)
      const responseData = response.data || response
      
      // Cache the result
      const cacheKey = this.getCacheKey(item.query, item.variables)
      this.setCache(cacheKey, responseData)
      
      item.resolve(responseData)
    })
  }

  /**
   * Fetches data using GraphQL query with deduplication, batching, and caching
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @param {string} operationName - Operation name
   * @param {number} priority - Request priority (higher = more important)
   * @param {boolean} enableBatching - Whether to enable batching (default true)
   * @param {string|number|null} batchGroup - Group key for batching (e.g. section index). Queries in the same group batch together.
   * @returns {Promise}
   */
  async fetchGraphQL(query, variables = {}, operationName = null, priority = 0, enableBatching = true, batchGroup = null) {
    const cacheKey = this.getCacheKey(query, variables)
    
    // Check cache first
    const cached = this.getCached(cacheKey)
    if (cached !== null) {
      return cached
    }

    // Check for pending request (deduplication)
    const pendingKey = `${cacheKey}:${operationName || 'default'}`
    if (this.pendingRequests.has(pendingKey)) {
      return this.pendingRequests.get(pendingKey)
    }

    // Create request promise
    const requestPromise = this.createRequestPromise(
      query,
      variables,
      operationName,
      priority,
      enableBatching,
      pendingKey,
      batchGroup
    )

    // Store pending request
    this.pendingRequests.set(pendingKey, requestPromise)

    // Clean up pending request when done
    requestPromise.finally(() => {
      this.pendingRequests.delete(pendingKey)
    })

    return requestPromise
  }

  /**
   * Creates a request promise
   * @param {string} query - GraphQL query
   * @param {Object} variables - Query variables
   * @param {string} operationName - Operation name
   * @param {number} priority - Priority
   * @param {boolean} enableBatching - Enable batching
   * @param {string} pendingKey - Pending request key
   * @returns {Promise}
   */
  createRequestPromise(query, variables, operationName, priority, enableBatching, pendingKey, batchGroup = null) {
    if (enableBatching) {
      return new Promise((resolve, reject) => {
        // Add to batch queue
        this.batchQueue.push({
          query,
          variables,
          operationName,
          priority,
          batchGroup,
          resolve,
          reject
        })

        // Schedule batch processing
        if (this.batchTimeout) {
          clearTimeout(this.batchTimeout)
        }
        this.batchTimeout = setTimeout(() => {
          this.processBatchQueue()
        }, this.batchDelay)
      })
    } else {
      // Execute immediately without batching
      return this.executeSingleQuery(query, variables, operationName, priority)
    }
  }

  /**
   * Executes a single GraphQL query
   * @param {string} query - GraphQL query
   * @param {Object} variables - Query variables
   * @param {string} operationName - Operation name
   * @param {number} priority - Priority
   * @returns {Promise}
   */
  async executeSingleQuery(query, variables, operationName, priority) {
    // Check rate limits
    if (!rateLimitManager.canMakeRequest()) {
      return rateLimitManager.queueRequest(
        () => this.executeQuery(query, variables, operationName),
        priority
      )
    }

    return this.executeQuery(query, variables, operationName)
  }

  /**
   * Executes a GraphQL query and updates cache
   * @param {string} query - GraphQL query
   * @param {Object} variables - Query variables
   * @param {string} operationName - Operation name
   * @returns {Promise}
   */
  async executeQuery(query, variables, operationName) {
    try {
      const result = await executeGraphQLQuery(query, variables, operationName)
      
      // Update rate limit manager
      if (result.headers) {
        rateLimitManager.updateFromHeaders(result.headers)
      }
      
      rateLimitManager.decrement()

      // Check for GraphQL errors
      if (result.data?.errors) {
        throw new Error(`GraphQL Error: ${JSON.stringify(result.data.errors)}`)
      }

      const responseData = result.data?.data || result.data

      // Cache the result
      const cacheKey = this.getCacheKey(query, variables)
      this.setCache(cacheKey, responseData)

      return responseData
    } catch (error) {
      // Don't cache errors
      throw error
    }
  }

  /**
   * Forces processing of batch queue immediately
   */
  flushBatch() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout)
      this.batchTimeout = null
    }
    return this.processBatchQueue()
  }

  /**
   * Gets cache statistics
   * @returns {Object}
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      queuedBatches: this.batchQueue.length
    }
  }
}

// Export singleton instance
export const dataFetcher = new DataFetcher()
